import { bopApi } from '@/lib/api/client';
import { HTTP_BAD_REQUEST, isHttpStatus, isNotFoundError } from '@/lib/api/error';
import type { UserSegment } from '@/services/api/user-console';

// User console — Access tab wire contracts.
//
// SECURITY: bop-api derives the audit requester from the Okta token
// (getDetailsFromEvent) for EVERY endpoint in this file — the client must never
// send a requester/operator name. (Contrast with POST /v1/user/comment on the
// Audit tab, which does take the name in the body.)
//
// Not ported: POST /v1/auth0/block-user is owned by the Auth tab's control.
// Blocking goes through POST /v1/user/status with status 'blocked'.
// The Access tab's BLOCK AUTH checkbox rides `block_auth` on POST /v1/user/status,
// which the API honours in both directions for any status.

const AUTH_STATUS_PATH = (pid: string) => `v1/auth0/${pid}/status`;
const USER_STATUS_PATH = 'v1/user/status';
const FEATURE_RESTRICTIONS_PATH = 'v1/user/feature-restrictions';
const FUNDS_CUSTOMER_PATH = (pid: string) => `v1/user/funds/customer/${pid}`;
const REMOVE_PENDING_ITEM_PATH = 'v1/user/status/remove-pending-item';

/** Auth0 login state only — the account lifecycle lives on `ConsoleUser.state`. */
export const USER_AUTH_STATUSES = ['ACTIVE', 'BLOCKED'] as const;
export type UserAuthStatus = (typeof USER_AUTH_STATUSES)[number];

interface AuthStatusResponse {
  /** null for broker clients — they log in through their broker, so have no Auth0 user */
  status: UserAuthStatus | null;
}

/**
 * `GET /v1/auth0/{pid}/status`. null = "no auth status": broker clients (200, status null)
 * or a profile with no email (400 USER_MISSING_EMAIL) — never surfaced as an error.
 * Every other failure propagates.
 */
export const getAuthStatus = async (pid: string): Promise<UserAuthStatus | null> => {
  try {
    const response = await bopApi.get(AUTH_STATUS_PATH(pid)).json<AuthStatusResponse>();
    return response.status;
  } catch (error) {
    if (isHttpStatus(error, HTTP_BAD_REQUEST) && error.message === 'USER_MISSING_EMAIL') return null;
    throw error;
  }
};

/** `POST /v1/user/status` request body. */
export interface UpdateUserStatusRequest {
  comment: string;
  pid: string;
  /** LOWERCASE on the wire — the UI works in the UPPERCASE UserState enum. */
  status: string;
  /** Only honoured for blocked/offboarded/deleted: additionally blocks login in Auth0. */
  block_auth?: boolean;
}

export interface UserStatusUpdateInput {
  pid: string;
  comment: string;
  /** UPPERCASE UserState value — lowercased here. */
  state: string;
  /** Pass ONLY when the target state is restricted; leave undefined otherwise. */
  blockAuth?: boolean;
}

export const buildUserStatusRequest = ({ pid, comment, state, blockAuth }: UserStatusUpdateInput): UpdateUserStatusRequest => ({
  comment,
  pid,
  // Status lowercasing moved client-side from the Nuxt zod transform.
  status: state.toLowerCase(),
  // Always the snake_case key, INCLUDED only for restricted target
  // states — never send `block_auth: undefined`.
  ...(blockAuth === undefined ? {} : { block_auth: blockAuth }),
});

// 200 responses have an empty body — never .json() this call.
export const updateUserStatus = (body: UpdateUserStatusRequest) => bopApi.post(USER_STATUS_PATH, { json: body }).text();

export interface UpdateFeatureRestrictionsInput {
  pid: string;
  enabledSpot: boolean;
  enabledInstantTrade: boolean;
  enabledFutures: boolean;
  /** Omit unless the operator changed it — avoids clobbering a concurrently-set flag. */
  staff?: boolean;
  comment: string;
}

/**
 * The panel keeps `enabled_*` booleans; the wire wants the inverted
 * FULL-replacement list of RESTRICTED features — always all three decisions.
 */
export const buildFeatureRestrictions = ({
  enabledSpot,
  enabledInstantTrade,
  enabledFutures,
}: Pick<UpdateFeatureRestrictionsInput, 'enabledSpot' | 'enabledInstantTrade' | 'enabledFutures'>): string[] => [
  ...(enabledSpot ? [] : ['SPOT']),
  ...(enabledInstantTrade ? [] : ['INSTANT-TRADE']), // hyphen, not underscore
  ...(enabledFutures ? [] : ['FUTURES']),
];

/**
 * `POST /v1/user/feature-restrictions`. FIX: the 200 body is a bare
 * JSON-encoded string ("Feature restrictions updated"), not an object — read as
 * text and rely on ky throwing for non-2xx (legacy passed unexpected 200 bodies
 * silently).
 */
export const updateFeatureRestrictions = ({ pid, enabledSpot, enabledInstantTrade, enabledFutures, staff, comment }: UpdateFeatureRestrictionsInput) =>
  bopApi
    .post(FEATURE_RESTRICTIONS_PATH, {
      json: {
        pid,
        featureRestrictions: buildFeatureRestrictions({ enabledSpot, enabledInstantTrade, enabledFutures }),
        comment,
        ...(staff === undefined ? {} : { staff }),
      },
    })
    .text();

/**
 * The SINGLE home for this wire contract (the user-console.ts copy was dead code
 * and is deleted; the questionnaires slice consumes the endpoint via
 * getFundsCustomer below).
 */
export interface FundsCustomer {
  account_holder_id: string;
  account_holder_name?: string | null;
  segment?: UserSegment;
  aml_eur_threshold?: number;
  citizenship_country_code?: string | null;
  residence_country_code?: string | null;
  address?: string | null;
  enabled_fiat_withdrawals: boolean;
  enabled_fiat_deposits: boolean;
  enabled_crypto_deposits: boolean;
  enabled_crypto_withdrawals: boolean;
}

/**
 * A 404 ("No records found for PID …") is NORMAL for users with
 * no funds row yet — synthesize the all-enabled default. Any other failure
 * surfaces (legacy returned null and the panel silently broke — FIX).
 */
export const getFundsCustomer = async (pid: string): Promise<FundsCustomer> => {
  try {
    return await bopApi.get(FUNDS_CUSTOMER_PATH(pid)).json<FundsCustomer>();
  } catch (error) {
    if (isNotFoundError(error)) {
      return {
        account_holder_id: pid,
        enabled_fiat_withdrawals: true,
        enabled_fiat_deposits: true,
        enabled_crypto_deposits: true,
        enabled_crypto_withdrawals: true,
      };
    }
    throw error;
  }
};

/**
 * `POST /v1/user/funds/customer/{pid}` body. STRICT key whitelist: any other
 * key → 400 `Invalid property: {key}` — send ONLY the four `enabled_*` keys plus
 * `comment` (the legacy Nuxt route stripped an `action` key before forwarding).
 */
export interface UpdateFundsCustomerRequest {
  enabled_crypto_deposits: boolean;
  enabled_crypto_withdrawals: boolean;
  enabled_fiat_deposits: boolean;
  enabled_fiat_withdrawals: boolean;
  /** Audit-only; NOT forwarded to funds. (bop-api audits BEFORE verifying the funds result.) */
  comment: string;
}

export const updateFundsCustomer = (pid: string, body: UpdateFundsCustomerRequest) => bopApi.post(FUNDS_CUSTOMER_PATH(pid), { json: body }).json<FundsCustomer>();

/** `POST /v1/user/status/remove-pending-item` body. */
export interface RemovePendingItemRequest {
  comment: string;
  /** Exact string from user.pendingItems (400 PENDING_ITEM_NOT_FOUND otherwise). */
  pendingItem: string;
  pid: string;
}

// 200 responses have an empty body — never .json() this call.
export const removePendingItem = (body: RemovePendingItemRequest) => bopApi.post(REMOVE_PENDING_ITEM_PATH, { json: body }).text();
